import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Team } from "@/types";
import { useTeams, useCreateTeam, useUpdateTeam, useDeleteTeam } from "@/hooks/useTeams";
import { TeamForm } from "./TeamForm";
import { TeamMemberAssignment } from "./TeamMemberAssignment";
import { Plus, Search, Users, Edit, Trash2, UserPlus } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";

interface TeamManagementProps {
    isAdmin: boolean;
}

export function TeamManagement({ isAdmin }: TeamManagementProps) {
    const [searchQuery, setSearchQuery] = useState("");
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [editingTeam, setEditingTeam] = useState<Team | null>(null);
    const [deletingTeam, setDeletingTeam] = useState<Team | null>(null);
    const [managingTeam, setManagingTeam] = useState<Team | null>(null);

    const { toast } = useToast();
    const { data: teams, isLoading } = useTeams();
    const createTeam = useCreateTeam();
    const updateTeam = useUpdateTeam();
    const deleteTeam = useDeleteTeam();

    const teamList = teams?.data || [];
    const filteredTeams = teamList.filter(team =>
        team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        team.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );


    const handleCreateTeam = (data: { name: string; description?: string }) => {
        createTeam.mutate(data, {
            onSuccess: () => {
                toast({
                    title: "Team created",
                    description: "The team has been successfully created.",
                });
                setShowCreateDialog(false);
            },
            onError: () => {
                toast({
                    title: "Error",
                    description: "Failed to create team. Please try again.",
                    variant: "destructive",
                });
            },
        });
    };

    const handleUpdateTeam = (data: { name: string; description?: string }) => {
        if (!editingTeam) return;

        updateTeam.mutate(
            { teamPid: editingTeam.pid, data },
            {
                onSuccess: () => {
                    toast({
                        title: "Team updated",
                        description: "The team has been successfully updated.",
                    });
                    setEditingTeam(null);
                },
                onError: () => {
                    toast({
                        title: "Error",
                        description: "Failed to update team. Please try again.",
                        variant: "destructive",
                    });
                },
            }
        );
    };

    const handleDeleteTeam = () => {
        if (!deletingTeam) return;

        deleteTeam.mutate(deletingTeam.pid, {
            onSuccess: () => {
                toast({
                    title: "Team deleted",
                    description: "The team has been successfully deleted.",
                });
                setDeletingTeam(null);
            },
            onError: () => {
                toast({
                    title: "Error",
                    description: "Failed to delete team. Please try again.",
                    variant: "destructive",
                });
            },
        });
    };

    return (
        <div className="space-y-6">
            {/* Header with Search and Create */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search teams..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9"
                    />
                </div>
                {isAdmin && (
                    <Button variant="gradient" className="gap-2" onClick={() => setShowCreateDialog(true)}>
                        <Plus className="h-4 w-4" />
                        Create Team
                    </Button>
                )}
            </div>

            {/* Teams Grid */}
            {isLoading ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {[1, 2, 3].map((i) => (
                        <Card key={i}>
                            <CardHeader>
                                <Skeleton className="h-6 w-36 mb-2" />
                                <Skeleton className="h-4 w-48" />
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center justify-between">
                                    <Skeleton className="h-4 w-28" />
                                    <div className="flex gap-1">
                                        <Skeleton className="h-8 w-8 rounded-md" />
                                        <Skeleton className="h-8 w-8 rounded-md" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : filteredTeams.length === 0 ? (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                        <Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
                        <h3 className="text-lg font-semibold mb-2">
                            {searchQuery ? "No teams found" : "No teams yet"}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            {searchQuery
                                ? "Try adjusting your search query"
                                : isAdmin
                                    ? "Create your first team to get started"
                                    : "No teams have been created yet"}
                        </p>
                        {isAdmin && !searchQuery && (
                            <Button variant="gradient" className="gap-2" onClick={() => setShowCreateDialog(true)}>
                                <Plus className="h-4 w-4" />
                                Create Team
                            </Button>
                        )}
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {filteredTeams.map((team) => (
                        <Card key={team.pid} className="hover:shadow-md transition-shadow">
                            <CardHeader>
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <CardTitle className="text-lg">{team.name}</CardTitle>
                                        <CardDescription className="mt-1.5 line-clamp-2">
                                            {team.description || "No description"}
                                        </CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <Users className="h-4 w-4" />
                                        <span>Team members</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        {isAdmin && (
                                            <>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    onClick={() => setManagingTeam(team)}
                                                    title="Manage members"
                                                >
                                                    <UserPlus className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    onClick={() => setEditingTeam(team)}
                                                    title="Edit team"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                                    onClick={() => setDeletingTeam(team)}
                                                    title="Delete team"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Create/Edit Team Dialog */}
            <TeamForm
                open={showCreateDialog || !!editingTeam}
                onClose={() => {
                    setShowCreateDialog(false);
                    setEditingTeam(null);
                }}
                onSubmit={editingTeam ? handleUpdateTeam : handleCreateTeam}
                initialData={editingTeam || undefined}
                isLoading={createTeam.isPending || updateTeam.isPending}
            />

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={!!deletingTeam} onOpenChange={() => setDeletingTeam(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Team</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete <strong>{deletingTeam?.name}</strong>? This action cannot be undone.
                            All team assignments will be removed.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteTeam}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Manage Team Members Dialog */}
            {managingTeam && (
                <TeamMemberAssignment
                    open={!!managingTeam}
                    onClose={() => setManagingTeam(null)}
                    team={managingTeam}
                />
            )}
        </div>
    );
}
